import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare, Search, MousePointer, ExternalLink } from 'lucide-react';
import './HomePage.css';
import './NetSuitePage.css';

const LDM_LOGO = 'https://endavant-cdns.s3.us-east-2.amazonaws.com/web-ldm/img/logo-light.svg';

const steps = [
  {
    number: 1,
    title: 'Abre Google Chat',
    description: 'Ve a chat.google.com o abre Google Chat desde tu cuenta de Google Workspace.',
    icon: ExternalLink,
  },
  {
    number: 2,
    title: 'Busca "NetSuiteBot"',
    description: 'En el campo de búsqueda de chats, escribe netsuitebot y selecciona la app "NetSuiteBot" que aparece en los resultados.',
    icon: Search,
  },
  {
    number: 3,
    title: 'Inicia una conversación',
    description: 'Haz clic en NetSuiteBot y comienza a hacer preguntas sobre datos de NetSuite. El bot consultará Databricks Lakehouse para darte respuestas precisas.',
    icon: MousePointer,
  },
];

export default function NetSuitePage() {
  const navigate = useNavigate();

  return (
    <div className="labs-page">
      <div className="labs-bg-grid" />

      {/* Header */}
      <header className="labs-header">
        <div className="labs-header-inner">
          <div className="labs-logo-group" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={LDM_LOGO} alt="LDM" className="labs-logo-img" />
            <span className="labs-logo-text">labs</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="ns-page">
        <motion.div
          className="ns-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back button */}
          <button
            className="ns-back"
            onClick={() => navigate('/')}
            data-testid="back-to-labs"
          >
            <ArrowLeft size={16} />
            Volver a Labs
          </button>

          {/* Hero */}
          <div className="ns-hero">
            <div className="ns-hero-icon">
              <MessageSquare size={32} color="white" />
            </div>
            <h1 className="ns-hero-title">Asistente NetSuite</h1>
            <p className="ns-hero-subtitle">
              Agente de IA que consulta datos de NetSuite. Disponible como bot en Google Chat.
            </p>
            <div className="labs-tags" style={{ justifyContent: 'center' }}>
              <span className="labs-tag">Databricks Lakehouse</span>
              <span className="labs-tag">Genie</span>
              <span className="labs-tag">Google AppScripts</span>
            </div>
          </div>

          {/* Instructions */}
          <div className="ns-instructions">
            <h2 className="ns-instructions-title">Cómo acceder</h2>

            <div className="ns-steps">
              {steps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    className="ns-step"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 * (i + 1) }}
                  >
                    <div className="ns-step-number">{step.number}</div>
                    <div className="ns-step-content">
                      <div className="ns-step-header">
                        <Icon size={18} className="ns-step-icon" />
                        <h3 className="ns-step-title">{step.title}</h3>
                      </div>
                      <p className="ns-step-desc">{step.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Disclaimer */}
            <div className="ns-disclaimer">
              <strong>¿No encuentras el bot?</strong> Si NetSuiteBot no aparece en tu Google Chat, solicita acceso al admin de Labs: <strong>Rafael Strauss</strong>.
            </div>

            {/* CTA */}
            <motion.a
              href="https://chat.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="ns-cta"
              data-testid="open-gchat-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <MessageSquare size={18} />
              Abrir Google Chat
              <ExternalLink size={14} />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="labs-footer">
        <div className="labs-footer-inner">
          <img src={LDM_LOGO} alt="LDM" className="labs-footer-logo" />
          <span className="labs-footer-labs">labs</span>
          <p className="labs-footer-text">
            © {new Date().getFullYear()} LDM · Forward Thinking Marketing
          </p>
        </div>
      </footer>
    </div>
  );
}
